import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import {
  AppState,
  DashboardInstallationState,
  DashboardStoreStatusType,
  DashboardTileType,
  dashboardTileTypeToDashboardTileObjectMapper,
  GenericStoreStatus,
} from '@store';
import { DashboardDevice, DashboardInstallationData } from '@vi-iot/client';

import { DashboardTileOrder } from './dashboard.model';
import { DashboardState } from './dashboard.state';

export namespace DashboardSelectors {
  export const dashboard = (state: AppState): DashboardState => state.dashboard;

  export function getState(): MemoizedSelector<
    AppState,
    DashboardState,
    DefaultProjectorFn<DashboardState>
  > {
    return createSelector(dashboard, (state: DashboardState) => state);
  }

  export const getTilesOrderLoading = createSelector(
    dashboard,
    (state: DashboardState): boolean => state.tilesOrder.loading
  );

  export const selectFetchingDashboardStatus = createSelector(
    dashboard,
    (state: DashboardState): GenericStoreStatus => state.fetchingDashboardStatus
  );

  export const getTilesOrderReloadGrid = createSelector(
    dashboard,
    (state: DashboardState): boolean => state.tilesOrder.reloadGrid
  );

  export const getTilesOrder = createSelector(
    dashboard,
    (state: DashboardState): DashboardTileOrder[] => state.tilesOrder.value
  );

  export function getInstallationsState(
    tileType: DashboardTileType,
    tabStateName: string = null
  ): MemoizedSelector<
    AppState,
    DashboardInstallationState,
    DefaultProjectorFn<DashboardInstallationState>
  > {
    return createSelector(dashboard, (state: DashboardState) =>
      tabStateName
        ? state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].value[
            tabStateName
          ]
        : state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState]
    );
  }

  export function getStoreStatus(
    tileType: DashboardTileType,
    tabStateName: string = null
  ): MemoizedSelector<
    AppState,
    DashboardStoreStatusType,
    DefaultProjectorFn<DashboardStoreStatusType>
  > {
    return createSelector(dashboard, (state: DashboardState) => {
      const initializing = tabStateName
        ? state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].value[
            tabStateName
          ].initializing
        : state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].initializing;

      if (initializing) {
        return DashboardStoreStatusType.Initializing;
      }

      const tabError = tabStateName
        ? state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].value[
            tabStateName
          ].error
        : false;

      if (tabError) {
        return DashboardStoreStatusType.ErrorTab;
      }

      const tileError = dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs
        ? !dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs.some(
            (tab) =>
              state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].value[
                tab.storeState
              ].error === null
          )
        : state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].error;

      if (tileError) {
        return DashboardStoreStatusType.ErrorTile;
      }

      const tabDataEmpty = tabStateName
        ? state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].value[
            tabStateName
          ].value.length === 0
        : false;

      if (tabDataEmpty) {
        return DashboardStoreStatusType.EmptyTab;
      }

      const tileDataEmpty = dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs
        ? !dashboardTileTypeToDashboardTileObjectMapper[tileType].tabs.some(
            (tab) =>
              state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].value[
                tab.storeState
              ].value.length > 0
          )
        : state[dashboardTileTypeToDashboardTileObjectMapper[tileType].storeState].value.length ===
          0;

      if (tileDataEmpty) {
        return DashboardStoreStatusType.EmptyTile;
      }

      return DashboardStoreStatusType.Done;
    });
  }

  export function getColumnInstallationsCounter(
    name: string
  ): MemoizedSelector<AppState, number, DefaultProjectorFn<number>> {
    return createSelector(dashboard, (state: DashboardState) => {
      return (
        state.columns.find((column) => column.name.toLowerCase() === name.toLowerCase())
          .installationIds?.length ?? 0
      );
    });
  }

  export function getColumnInstallations(
    name: string
  ): MemoizedSelector<
    AppState,
    DashboardInstallationData[],
    DefaultProjectorFn<DashboardInstallationData[]>
  > {
    return createSelector(dashboard, (state: DashboardState) => {
      return state.columns.find((column) => column.name.toLowerCase() === name.toLowerCase())
        .installations;
    });
  }

  export const selectDashboardColumnsPresence = createSelector(
    dashboard,
    (state: DashboardState): boolean => !!state.columns
  );

  export function getDashboardDevicesById(
    id: number
  ): MemoizedSelector<AppState, DashboardDevice[], DefaultProjectorFn<DashboardDevice[]>> {
    return createSelector(dashboard, (state: DashboardState) => {
      const installation = state.columns.find((col) => col.installationIds.includes(id));
      const devices = installation?.installations.find((inst) => inst.id === id).devices;

      return devices || [];
    });
  }

  export const selectIsWhatsAppBannerVisible = createSelector(
    dashboard,
    (state: DashboardState): boolean => state.isWhatsAppBannerVisible
  );
}
